import React, { useState, useEffect } from 'react';
import { sanitizeHtml } from '../../utils/sanitize';
import {
  Card,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Box,
  CircularProgress,
  Checkbox,
  Collapse,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert } from
'@mui/material';
import { ExpandMore, CheckCircleOutline, Lightbulb } from '@mui/icons-material';

/**
 * Component that recommends solutions based on ticket description
 * @param {string} ticketDescription - The description text of the ticket
 * @param {function} onSolutionsFound - Callback when solutions are found
 * @param {boolean} isCreatingTicket - Whether this is during ticket creation or viewing
 * @param {function} onSolutionSelect - Callback when a solution is selected (for support staff)
 * @param {boolean} isSupport - Whether the current user is support staff
 */import { useThemeSettings } from "../../contexts/ThemeContext";
export function SolutionRecommendation({
  ticketDescription,
  onSolutionsFound,
  isCreatingTicket = false,
  onSolutionSelect,
  isSupport = false
}) {const { t } = useThemeSettings();
  const [loading, setLoading] = useState(false);
  const [similarIssues, setSimilarIssues] = useState([]);
  const [expandedIssue, setExpandedIssue] = useState(null);
  const [expandedSolution, setExpandedSolution] = useState(null);
  const [selectedSolutions, setSelectedSolutions] = useState([]);
  const [noResultsFound, setNoResultsFound] = useState(false);

  // ExpandMore icon component
  const ExpandMoreIcon = () => <span>▼</span>;

  // Other icon components
  const LightbulbIcon = () => <span>💡</span>;
  const CheckCircleOutlineIcon = () => <span>✓</span>;

  useEffect(() => {
    // Only search for solutions if description is long enough
    if (ticketDescription && ticketDescription.length > 20) {
      setLoading(true);
      setNoResultsFound(false);

      // In a real implementation, this would call an API to find similar issues
      // For now, simulate an API call with a delay
      setTimeout(() => {
        // Mock similar issues with solutions
        const mockIssues = [
        {
          issue: {
            id: 1,
            title: 'Tablet unresponsive',
            description: 'The tablet is not responding to touch inputs or button presses.'
          },
          solutions: [
          {
            id: 1,
            title: 'Restart the device',
            description: 'Power off the device completely, wait 30 seconds, then power it back on.',
            stepByStepInstructions: '<p>1. Press and hold the power button until the shutdown menu appears</p><p>2. Select "Power Off"</p><p>3. Wait 30 seconds</p><p>4. Press the power button to turn the device back on</p>',
            complexity: 'simple',
            totalUses: 100,
            successCount: 85
          },
          {
            id: 2,
            title: 'Factory reset the tablet',
            description: 'Perform a factory reset as a last resort for severe issues.',
            stepByStepInstructions: '<p>1. Power off the device</p><p>2. Press and hold Volume Up + Power buttons until recovery menu appears</p><p>3. Use volume buttons to navigate to "Factory Reset" and press Power to select</p><p>4. Confirm the reset</p>',
            complexity: 'complex',
            totalUses: 15,
            successCount: 15
          }],

          score: 0.85
        },
        {
          issue: {
            id: 2,
            title: 'App crashing repeatedly',
            description: 'The Caro app crashes immediately after launch or during use.'
          },
          solutions: [
          {
            id: 3,
            title: 'Clear app cache and data',
            description: 'Clear the app cache and data to resolve issues with app functionality.',
            stepByStepInstructions: '<p>1. Go to Settings</p><p>2. Select "Apps" or "Applications"</p><p>3. Find and select the Caro app</p><p>4. Select "Storage"</p><p>5. Tap "Clear Cache" and then "Clear Data"</p>',
            complexity: 'moderate',
            totalUses: 40,
            successCount: 30
          },
          {
            id: 4,
            title: 'Reinstall the Caro app',
            description: 'Uninstall and reinstall the Caro app to resolve persistent issues.',
            stepByStepInstructions: '<p>1. Go to Settings</p><p>2. Select "Apps" or "Applications"</p><p>3. Find and select the Caro app</p><p>4. Select "Uninstall"</p><p>5. Download and install the app again from the provided source</p>',
            complexity: 'moderate',
            totalUses: 30,
            successCount: 20
          }],

          score: 0.65
        },
        {
          issue: {
            id: 3,
            title: 'Network connectivity issues',
            description: 'Device is unable to connect to the network or has intermittent connectivity.'
          },
          solutions: [
          {
            id: 5,
            title: 'Check network connectivity',
            description: 'Verify that the device is properly connected to the network.',
            stepByStepInstructions: '<p>1. Go to Settings</p><p>2. Select "Wi-Fi" or "Network"</p><p>3. Check if the device is connected to the correct network</p><p>4. If not, select the correct network and enter credentials if needed</p>',
            complexity: 'simple',
            totalUses: 60,
            successCount: 45
          },
          {
            id: 6,
            title: 'Reset network settings',
            description: 'Reset all network settings to resolve persistent connectivity issues.',
            stepByStepInstructions: '<p>1. Go to Settings</p><p>2. Select "System" or "General Management"</p><p>3. Select "Reset" or "Reset options"</p><p>4. Select "Reset network settings"</p><p>5. Confirm the reset</p>',
            complexity: 'moderate',
            totalUses: 25,
            successCount: 18
          }],

          score: 0.55
        }];


        // Filter issues that match the description
        const matchedIssues = mockIssues.filter((issue) => {
          // Simple text matching logic - in a real implementation, this would be more sophisticated
          const keywords = ticketDescription.toLowerCase().split(/\s+/);
          const issueText = (issue.issue.title + ' ' + issue.issue.description).toLowerCase();
          return keywords.some((keyword) => keyword.length > 3 && issueText.includes(keyword));
        });

        setSimilarIssues(matchedIssues);

        if (matchedIssues.length > 0) {
          const allSolutions = [];
          matchedIssues.forEach((issue) => {
            if (issue.solutions && issue.solutions.length > 0) {
              issue.solutions.forEach((solution) => {
                allSolutions.push({
                  ...solution,
                  issueId: issue.issue.id,
                  issueTitle: issue.issue.title
                });
              });
            }
          });

          onSolutionsFound && onSolutionsFound(allSolutions);

          if (allSolutions.length === 0) {
            setNoResultsFound(true);
          }
        } else {
          setNoResultsFound(true);
        }

        setLoading(false);
      }, 1500);
    }
  }, [ticketDescription, onSolutionsFound]);

  const handleExpandIssue = (issueId) => {
    setExpandedIssue(expandedIssue === issueId ? null : issueId);
  };

  const handleExpandSolution = (solutionId) => {
    setExpandedSolution(expandedSolution === solutionId ? null : solutionId);
  };

  const handleSelectSolution = (solution) => {
    const exists = selectedSolutions.some((s) => s.id === solution.id);

    if (exists) {
      setSelectedSolutions(selectedSolutions.filter((s) => s.id !== solution.id));
    } else {
      setSelectedSolutions([...selectedSolutions, solution]);
    }
  };

  const handleApplySolutions = () => {
    if (selectedSolutions.length > 0 && onSolutionSelect) {
      onSolutionSelect(selectedSolutions.map((s) => s.id));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={2}>
        <CircularProgress size={24} />
        <Typography variant="body2" ml={2}>{t ? t("SolutionRecommendation.searching_similar_issues_solutio") : "Searching for similar issues and solutions..."}</Typography>
      </Box>);

  }

  if (noResultsFound || similarIssues.length === 0) {
    return null;
  }

  // Count the total number of solutions across all issues
  const totalSolutions = similarIssues.reduce(
    (count, issue) => count + (issue.solutions ? issue.solutions.length : 0),
    0
  );

  // If no solutions found despite having similar issues
  if (totalSolutions === 0) {
    return (
      <Card sx={{ p: 2, mt: 2 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <LightbulbIcon />
          <Typography variant="h6" color="primary" sx={{ ml: 1 }}>{t ? t("SolutionRecommendation.similar_issues_found") : "Similar Issues Found"}

          </Typography>
        </Box>
        
        <Alert severity="info">{t ? t("SolutionRecommendation.we_found_similar_issues_our_database") : "We found similar issues in our database, but no validated solutions are available yet."}

          {isCreatingTicket && " You might want to provide more details or contact support directly."}
        </Alert>
      </Card>);

  }

  return (
    <Card sx={{ p: 2, mt: 2 }}>
      <Box display="flex" alignItems="center" mb={2}>
        <LightbulbIcon />
        <Typography variant="h6" color="primary" sx={{ ml: 1 }}>
          {isCreatingTicket ?
          "Similar Issues Found - Solutions Available" :
          "Recommended Solutions"}
        </Typography>
        
        {isSupport && selectedSolutions.length > 0 &&
        <Button
          variant="contained"
          color="primary"
          sx={{ ml: 'auto' }}
          onClick={handleApplySolutions}>{t ? t("SolutionRecommendation.apply") : "Apply"}

          {selectedSolutions.length}{t ? t("SolutionRecommendation.selected_solution") : "Selected Solution"}{selectedSolutions.length > 1 ? 's' : ''}
          </Button>
        }
      </Box>
      
      <Typography variant="body2" color="text.secondary" mb={2}>
        {isCreatingTicket ?
        "We found similar issues with possible solutions. Consider trying these before submitting." :
        "Based on the ticket description, these solutions might help resolve the issue:"}
      </Typography>
      
      <List sx={{ width: '100%' }}>
        {similarIssues.slice(0, 3).map((issueData) => {
          const issue = issueData.issue;
          const solutions = issueData.solutions || [];
          const hasSolutions = solutions && solutions.length > 0;

          if (!hasSolutions) return null;

          return (
            <React.Fragment key={issue.id}>
              <Accordion
                expanded={expandedIssue === issue.id}
                onChange={() => handleExpandIssue(issue.id)}
                sx={{ mb: 2 }}>
                
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box width="100%">
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" fontWeight="bold">
                        {issue.title}
                      </Typography>
                      <Chip
                        label={`Match: ${Math.round(issueData.score * 100)}%`}
                        color={issueData.score > 0.7 ? "success" : issueData.score > 0.4 ? "warning" : "default"}
                        size="small" />
                      
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {solutions.length}{t ? t("SolutionRecommendation.potential_solution") : "potential solution"}{solutions.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails>
                  <Typography variant="body2" mb={2}>
                    {issue.description}
                  </Typography>
                  
                  <Typography variant="subtitle2" gutterBottom>{t ? t("SolutionRecommendation.available_solutions") : "Available Solutions:"}

                  </Typography>
                  
                  <List dense>
                    {solutions.map((solution) =>
                    <Box key={solution.id} sx={{ mb: 1.5 }}>
                        <Accordion
                        expanded={expandedSolution === solution.id}
                        onChange={() => handleExpandSolution(solution.id)}
                        sx={{
                          bgcolor: 'background.default',
                          boxShadow: 'none',
                          '&:before': { display: 'none' },
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1
                        }}>
                        
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box display="flex" width="100%" alignItems="center">
                              {isSupport &&
                            <Checkbox
                              checked={selectedSolutions.some((s) => s.id === solution.id)}
                              onChange={() => handleSelectSolution(solution)}
                              onClick={(e) => e.stopPropagation()}
                              color="primary"
                              size="small"
                              sx={{ mr: 1 }} />

                            }
                              <ListItemText
                              primary={solution.title}
                              secondary={
                              <React.Fragment>
                                    <Typography variant="caption" component="span">{t ? t("SelectExistingSolution.success_rate") : "Success rate:"}
                                  {
                                  solution.totalUses > 0 ?
                                  `${Math.round(solution.successCount / solution.totalUses * 100)}%` :
                                  'New'
                                  }
                                      {solution.totalUses > 0 && ` (${solution.successCount}/${solution.totalUses})`}
                                    </Typography>
                                    <Chip
                                  label={solution.complexity || 'Moderate'}
                                  size="small"
                                  variant="outlined"
                                  sx={{ ml: 1, height: 18, fontSize: '0.625rem' }} />
                                
                                  </React.Fragment>
                              } />
                            
                            </Box>
                          </AccordionSummary>
                          
                          <AccordionDetails>
                            <Typography variant="body2" gutterBottom>
                              {solution.description}
                            </Typography>
                            
                            {solution.stepByStepInstructions &&
                          <>
                                <Typography variant="subtitle2" gutterBottom mt={1}>{t ? t("SolutionRecommendation.stepbystep_instructions") : "Step-by-step instructions:"}

                            </Typography>
                                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(solution.stepByStepInstructions) }} />
                              </>
                          }
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    )}
                  </List>
                </AccordionDetails>
              </Accordion>
            </React.Fragment>);

        })}
      </List>
      
      {isCreatingTicket &&
      <Box mt={2} display="flex" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">{t ? t("SolutionRecommendation.have_you_tried_these_solutions_before_su") : "Have you tried these solutions before submitting this ticket?"}

        </Typography>
          <Button
          variant="outlined"
          color="primary"
          onClick={() => alert('This would navigate to the knowledge base')}>{t ? t("SolutionRecommendation.view_all_solutions") : "View All Solutions"}


        </Button>
        </Box>
      }
    </Card>);

}